import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import { from, switchMap } from 'rxjs';
import type { Product } from './_cms/products/entities/Product.js';
import { ProductCHF } from './_cms/products/entities/ProductCHF.js';
import { Adresse } from './adresse.js';
import { KPF } from './master-data-management/kpf/entities/KPF.js';
import { Fundingpot } from './master-data-management/partner/entities/Fundingpot.js';
import { FundingpotFunding } from './master-data-management/partner/entities/FundingpotFunding.js';
import { Partner } from './master-data-management/partner/entities/Partner.js';

@Entity()
export class LogistikAccessToken extends MongoEntity {
  static override _class = 'LogistikAccessToken';
  static override _collectionName = 'logistik-access-tokens';

  /** gets filled beforeCreate */
  @Field(() => Int)
  code!: number;

  @validate(nonOptional())
  @Field(() => DateTime)
  validUntil!: DateTime;

  @Field(() => Int)
  maxOrderQuantity: Maybe<number>;

  static override async beforeCreate(entity: LogistikAccessToken) {
    const tokenCol = await getRawCollection(this._collectionName);

    if (!entity.has('code') || !entity.code) {
      let newCode: number;
      do {
        newCode = +(Math.random() * 1000000).toFixed(0).slice(0, 6);
      } while (await tokenCol.countDocuments({ code: newCode }));
      entity.code = newCode;
    }

    if (await tokenCol.countDocuments({ code: entity.code }))
      throw new Error('Cannot use this code as it is already used by another token');

    return super.beforeCreate(entity);
  }

  static async checkCode(code: number, againstDate?: DateTime) {
    const col = await getRawCollection<EntityData<LogistikAccessToken>>(LogistikAccessToken._collectionName);
    const doc = await col.findOne({
      code,
      validUntil: {
        $gte: (againstDate || DateTime.now()).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toJSDate(),
      },
    });
    return doc !== null;
  }

  @Query(() => LogistikAccessToken)
  static getByToken<T extends LogistikAccessToken>(@Arg('token', () => Int) token: number) {
    return async (gqlFields: BaseQLSelectionSet<LogistikAccessToken>) => {
      const col = await getRawCollection<EntityData<LogistikAccessToken>>(this._collectionName);
      return col.findOne(
        { code: token },
        { projection: { code: 0, validUntil: 0, validityInMonths: 0 } },
      ) as unknown as Promise<T>;
    };
  }
}

@Entity()
export class LogistikSchoolAccessToken extends LogistikAccessToken {
  static override _class = 'LogistikSchoolAccessToken';

  // 3 levels needed for address with zipCity
  static override get allFields() {
    return selectionSet(() => this, 3);
  }

  static override async beforeCreate(entity: LogistikSchoolAccessToken): Promise<void> {
    await super.beforeCreate(entity);
    await LogistikSchoolAccessToken._ensureKpfWithChfExists(entity);
  }

  static override async transformBeforeUpdate(
    oldEntity: EntityData<LogistikSchoolAccessToken>,
    newEntity: LogistikSchoolAccessToken,
  ): Promise<void> {
    await super.transformBeforeUpdate(oldEntity, newEntity);
    await LogistikSchoolAccessToken._ensureKpfWithChfExists(newEntity);
  }

  private static async _ensureKpfWithChfExists(token: LogistikSchoolAccessToken): Promise<void> {
    // Planio-3624: Das Produkt "Hefte" soll direkt für die Token angelegt werden.
    const coll = await getCollection<KPF<Product>>(KPF._class);
    const numCHFKpfs = await coll.countDocuments({ schoolID: token.schoolID, 'produkt._class': ProductCHF._class });
    if (numCHFKpfs > 0) return;
    const kpf = new KPF<ProductCHF>({ schoolID: token.schoolID, produkt: new ProductCHF({}) });
    await kpf.create();
  }

  @validate(nonOptional())
  @Field(() => ID)
  schoolID!: string;

  @validate(nonOptional())
  @Field(() => String)
  schoolName: Maybe<string>;

  @Field(() => Adresse, { default: v => v ?? new Adresse({}) })
  schoolAddress: Maybe<Adresse>;

  @Field(() => Partner, {
    resolve: function (this: LogistikSchoolAccessToken) {
      return async () => {
        const col = await getRawCollection(KPF._collectionName);
        const agg = [
          { $match: { schoolID: this.schoolID } },
          {
            $lookup: {
              from: FundingpotFunding._collectionName,
              localField: '_id',
              foreignField: 'kpfID',
              as: 'fundingpotFunding',
            },
          },
          { $unwind: '$fundingpotFunding' },
          {
            $lookup: {
              from: Fundingpot._collectionName,
              localField: 'fundingpotFunding.fundingpotID',
              foreignField: '_id',
              as: 'fundingpot',
            },
          },
          { $unwind: '$fundingpot' },
          {
            $lookup: {
              from: Partner._collectionName,
              localField: 'fundingpot.partnerID',
              foreignField: '_id',
              as: 'fundingPartner',
            },
          },
          { $unwind: '$fundingPartner' },
          { $project: { fundingPartnerID: '$fundingPartner._id' } },
          { $limit: 1 },
        ];
        const r = await col.aggregate<{ fundingPartnerID: string }>(agg).toArray();
        if (!r.length || !r[0]?.fundingPartnerID) return null;
        return loadMongoData(Partner, r[0].fundingPartnerID) as Promise<Maybe<Partner>>;
      };
    },
  })
  foundation: Maybe<Partner>;

  @Field(() => String)
  zuHaenden: Maybe<string>;
}

const validateLogistikSchoolAccessToken = registerQuery({
  type: () => Boolean,
  operationName: 'validateLogistikSchoolAccessToken',
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
  params: [{ type: () => Int, name: 'code' }],
  resolve: (code: number) => () => LogistikSchoolAccessToken.checkCode(code),
});

const subscribeLogistikSchoolAccessTokenPaginatedFiltered = registerSubscription({
  type: () => [LogistikSchoolAccessToken],
  operationName: 'subscribeLogistikSchoolAccessTokenPaginatedFiltered',
  options: {},
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
  resolve:
    (skip: number, limit: number, filter: AdornisFilter) =>
    <SelSet extends BaseQLSelectionSet<LogistikSchoolAccessToken>>(gqlFields: SelSet) =>
      watchQuery(LogistikSchoolAccessToken._collectionName, {}).pipe(
        switchMap(() => {
          const codeFilterAgg = (() => {
            const mf = filter.getFilterAspect<StringAspect>('code')?.getMongoFilter()?.['code'];
            if (!mf) return [];
            return [{ $addFields: { codeAsString: { $toString: '$code' } } }, { $match: { codeAsString: mf } }];
          })();
          filter.aspects = filter.aspects.filter(a => a.fieldName !== 'code');
          const mf = filter?.getMongoFilter();
          const agg = [
            {
              $match: {
                isPublished: { $ne: false },
                _class: { $in: baseqlMetaData.entities.validSubclasses(LogistikSchoolAccessToken) },
              },
            },
            ...codeFilterAgg,
            ...(mf ? [{ $match: mf }] : []),
            { $sort: filter.getMongoOptions().sort },
            { $skip: skip },
            { $limit: limit },
          ];
          return from(
            getRawCollection<LogistikSchoolAccessToken>(LogistikSchoolAccessToken._collectionName).then(col =>
              col.aggregate<LogistikSchoolAccessToken>(agg).toArray(),
            ),
          );
        }),
      ),
});

export namespace LogistikSchoolAccessTokenAPI {
  export const validate = validateLogistikSchoolAccessToken;
  export const subscribePaginatedFiltered = subscribeLogistikSchoolAccessTokenPaginatedFiltered;
}
